/**
 * GlyphPathDecoder - Utility for decoding binary glyph path data from WASM format
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility decodes binary glyph path data received from WASM into JavaScript
 * path operations (MoveTo, LineTo, QuadTo, CubicTo, ClosePath).
 */

/**
 * Glyph path operation types
 */
export type GlyphPathOp = 
  | { type: 'M'; x: number; y: number }  // MoveTo
  | { type: 'L'; x: number; y: number }  // LineTo
  | { type: 'Q'; x1: number; y1: number; x: number; y: number }  // QuadTo
  | { type: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }  // CubicTo
  | { type: 'Z' };  // ClosePath

/**
 * Result of glyph path decoding
 */
export interface DecodedGlyphPath {
  operations: GlyphPathOp[];
  isEmpty: boolean;
}

/**
 * Quantize font size for cache key stability
 * 
 * Rounds size to nearest 1/64th to ensure consistent cache keys
 * (must match EngineTextRenderer.ts)
 * 
 * @param size - Font size in pixels
 * @returns Quantized size
 */
export function quantizeSize(size: number): number {
  return Math.round(size * 64) / 64;
}

/**
 * Generate glyph cache key
 * 
 * Format: `${fontUid}@${quantizedSize}:${gid}`
 * 
 * @param fontUid - Font UID
 * @param sizePx - Font size in pixels (will be quantized)
 * @param gid - Glyph ID
 * @returns Cache key string
 */
export function generateGlyphCacheKey(fontUid: string, sizePx: number, gid: number): string {
  const sizeKey = quantizeSize(sizePx);
  return `${fontUid}@${sizeKey}:${gid}`;
}

/**
 * Decode binary glyph path data from WASM format
 * 
 * WASM format structure:
 * [n_ops: u32][n_floats: u32][tags_len: u32][tags: u8[]][coords: f32[]]
 * 
 * Tag values:
 * - 0: MoveTo (M)
 * - 1: LineTo (L)
 * - 2: QuadTo (Q)
 * - 3: CubicTo (C)
 * - Other: ClosePath (Z)
 * 
 * @param buffer - ArrayBuffer containing binary glyph path data
 * @returns Decoded glyph path with operations and empty flag
 */
export function decodeGlyphPath(buffer: ArrayBuffer): DecodedGlyphPath {
  const view = new DataView(buffer);
  let offset = 0;

  // Read header: number of operations, floats, and tags length
  const numOps = view.getUint32(offset, true); offset += 4; // little-endian
  const numFloats = view.getUint32(offset, true); offset += 4;
  const tagsLen = view.getUint32(offset, true); offset += 4;

  // Check if glyph has no outline (empty operations) - early detection
  const isEmptyGlyph = numOps === 0 || numFloats === 0 || tagsLen === 0;

  let ops: GlyphPathOp[] = [];
  let coords: Float32Array = new Float32Array(0);

  if (!isEmptyGlyph) {
    // Only process operations for non-empty glyphs
    // Read tags as Uint8Array
    const tags = new Uint8Array(buffer, offset, tagsLen);
    offset += tagsLen;

    // Read coords as Float32Array - but offset might not be 4-byte aligned
    // Create a properly aligned buffer for the coordinates
    coords = new Float32Array(numFloats);
    for (let i = 0; i < numFloats; i++) {
      coords[i] = view.getFloat32(offset + (i * 4), true); // little-endian
    }

    // Decode operations
    let tagIdx = 0;
    let coordIdx = 0;

    for (let i = 0; i < numOps; i++) {
      const t = tags[tagIdx++];
      if (t === 0) { // M - MoveTo
        ops.push({ type: 'M', x: coords[coordIdx++], y: coords[coordIdx++] });
      } else if (t === 1) { // L - LineTo
        ops.push({ type: 'L', x: coords[coordIdx++], y: coords[coordIdx++] });
      } else if (t === 2) { // Q - QuadTo
        ops.push({ 
          type: 'Q', 
          x1: coords[coordIdx++], 
          y1: coords[coordIdx++], 
          x: coords[coordIdx++], 
          y: coords[coordIdx++] 
        });
      } else if (t === 3) { // C - CubicTo
        ops.push({ 
          type: 'C', 
          x1: coords[coordIdx++], 
          y1: coords[coordIdx++], 
          x2: coords[coordIdx++], 
          y2: coords[coordIdx++], 
          x: coords[coordIdx++], 
          y: coords[coordIdx++] 
        });
      } else { // Z - ClosePath
        ops.push({ type: 'Z' });
      }
    }
  }

  return {
    operations: ops,
    isEmpty: isEmptyGlyph
  };
}

